import React, { useEffect, useState } from "react";
import { motion, type Variants } from "motion/react";
import { useUser } from "@/providers/user-provider";
import { useAuth } from "@/providers/auth-provider";
import { fadeUp } from "@/motion/motion-variants";
import { springs } from "@/motion/motion-tokens";

const logoVariants: Variants = {
  hidden: { scale: 0.9, opacity: 0 },
  visible: { scale: 1, opacity: 1, transition: springs.bouncy },
  ready: {
    scale: [1, 1.12, 1],
    opacity: 1,
    transition: { duration: 0.45, times: [0, 0.5, 1], ease: "easeOut" },
  },
};

const glowVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 0.1, transition: { duration: 0.6, delay: 0.1 } },
  ready: { opacity: 0.22, transition: { duration: 0.3 } },
};

type InitialLoadingProps = {
  /** True once auth/user data has actually resolved (may still be held on screen). */
  isReady?: boolean;
};

export const InitialLoading: React.FC<InitialLoadingProps> = ({
  isReady = false,
}) => {
  const { state: userState } = useUser();
  const { state: authState } = useAuth();
  const [progress, setProgress] = useState(0);

  // Simulate progress bar based on states
  useEffect(() => {
    if (isReady) {
      // Ready beat: snap to full regardless of the underlying status blend.
      setProgress(100);
    } else if (authState.status === "loading") {
      setProgress(20);
    } else if (authState.status === "authenticated") {
      if (userState.status === "loading") {
        setProgress(60);
      } else if (
        userState.status === "loaded" ||
        userState.status === "not_registered"
      ) {
        setProgress(100);
      }
    } else if (authState.status === "error" || userState.status === "error") {
      // Keep progress at current or show error
    }
  }, [authState.status, userState.status, isReady]);

  return (
    <main
      className="mx-auto my-auto fixed inset-0 z-[100] flex flex-col items-center justify-center bg-pk-night-1"
      style={{
        height: "min(100svh, calc(480px * 18 / 9))",
        maxWidth: "480px",
      }}
    >
      {/* Background with some texture if possible, but keep it simple for now */}
      <div
        className="absolute inset-0 opacity-0"
        style={{
          backgroundImage: "url(/assets/app-ui/backgrounds/sky.jpg)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "grayscale(100%)",
        }}
      />

      <div className="relative z-10 flex flex-col items-center gap-8 w-full max-w-xs px-6">
        {/* Logo */}
        <div className="relative w-[380px] max-w-[90svh] h-auto ">
          {/* Glow effect */}
          <motion.div
            className="absolute inset-0 bg-pk-purple-3 blur-2xl rounded-full"
            variants={glowVariants}
            initial="hidden"
            animate={isReady ? "ready" : "visible"}
          />
          <motion.img
            src="/assets/app-ui/logo.png"
            alt="Logo"
            className="relative z-10 w-full h-full object-contain filter drop-shadow-[0_0_15px_rgba(122,122,122,0.25)]"
            variants={logoVariants}
            initial="hidden"
            animate={isReady ? "ready" : "visible"}
          />
        </div>

        {/* Loading Bar Container */}
        <motion.div
          className="w-full mt-4"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
        >
          <div className="flex justify-between items-end mb-2">
            <span className="text-sm font-bold text-pk-gray-4 uppercase tracking-widest animate-pulse">
              {progress < 100 ? "Loading..." : "Ready!"}
            </span>
            <span className="text-sm font-bold text-white opacity-50">
              {Math.round(progress)}%
            </span>
          </div>

          <div className="h-2 w-full bg-black/40 rounded-full overflow-hidden p-[2px] border border-white/10">
            <div
              className="h-full bg-gradient-to-r from-pk-gray-4 to-pk-gray-2 rounded-full transition-all duration-500 ease-out shadow-[0_0_10px_rgba(59,130,246,0.5)]"
              style={{ width: `${progress}%` }}
            />
          </div>
        </motion.div>

        {/* Status Messages */}
        <div className="h-4">
          {authState.status === "error" || userState.status === "error" ? (
            <p className="text-red-400 text-sm font-bold text-center">
              {authState.status === "error"
                ? authState.error
                : (userState as any).error}
            </p>
          ) : null}
        </div>
      </div>
    </main>
  );
};
